import { Star } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import type { SandboxImage } from '@/types/sandboxImage'

interface SandboxImagesTableProps {
  images: SandboxImage[]
  isLoading: boolean
}

export function SandboxImagesTable({ images, isLoading }: SandboxImagesTableProps) {
  if (isLoading) {
    return (
      <div
        className="p-6 text-center text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        Loading sandbox images...
      </div>
    )
  }

  if (images.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="sandbox-images-empty">
        No sandbox images. Catalog entries are seeded by the platform.
      </div>
    )
  }

  return (
    <Table aria-label="Sandbox images">
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead className="w-[130px]">Kind</TableHead>
          <TableHead>Image URI</TableHead>
          <TableHead className="w-[160px]">Runtimes</TableHead>
          <TableHead className="w-[130px]">Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {images.map((image) => (
          <TableRow key={image.id} data-testid={`sandbox-image-row-${image.id}`}>
            <TableCell className="font-medium">
              <span className="flex items-center gap-2">
                {image.isBaseline && (
                  <Star className="h-4 w-4 shrink-0 text-amber-500" aria-label="baseline" />
                )}
                <span className="min-w-0">{image.name}</span>
              </span>
              <span className="text-xs text-muted-foreground">{image.description}</span>
            </TableCell>
            <TableCell>
              <Badge variant="outline">{image.kind}</Badge>
            </TableCell>
            <TableCell className="font-mono text-xs">
              {image.imageUri}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {image.runtimes ?? '—'}
            </TableCell>
            <TableCell>
              {image.isDeprecated ? (
                <Badge variant="secondary">Deprecated</Badge>
              ) : (
                <Badge>Active</Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
